/**
 * <lk-flavor-carousel> Web Component — "discover the range" carousel (H2).
 *
 * Powered by vendored Embla Carousel (src/js/vendor/embla.js — same engine as
 * the Jack Link's sliders): mouse/touch dragging with momentum, click
 * suppression after a drag, looping, and center alignment all come from Embla.
 * This element only adds:
 *  - center-emphasis: the selected slide gets `.is-active` (larger via CSS)
 *  - prev/next arrow buttons -> scrollPrev/scrollNext
 *  - an aria-live status line announcing the active flavor
 *  - `prefers-reduced-motion`: programmatic scrolls become instant
 *
 * connectedCallback/disconnectedCallback ride shopify:section:load/unload, so
 * Embla rebuilds cleanly in the theme editor.
 *
 * Markup contract (sections/custom_lk-flavor-carousel.liquid):
 *   <lk-flavor-carousel>
 *     <button data-lk-prev> / <button data-lk-next>
 *     <div data-lk-embla>              <- Embla viewport (overflow hidden)
 *       <ul data-lk-scroller>          <- Embla container (flex)
 *         <li data-lk-card data-flavor="jalapeno">…</li>
 *     <p data-lk-status aria-live="polite">
 */

import { EmblaCarousel } from '../vendor/embla.js';

class LkFlavorCarousel extends HTMLElement {
  connectedCallback() {
    const viewport = this.querySelector('[data-lk-embla]');
    this.cards = Array.from(this.querySelectorAll('[data-lk-card]'));
    this.prevBtn = this.querySelector('[data-lk-prev]');
    this.nextBtn = this.querySelector('[data-lk-next]');
    this.statusEl = this.querySelector('[data-lk-status]');

    if (!viewport || this.cards.length === 0 || this.embla) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let startIndex = this.cards.findIndex((card) => card.classList.contains('is-active'));
    if (startIndex < 0) startIndex = Math.floor((this.cards.length - 1) / 2);

    this.embla = EmblaCarousel(viewport, {
      loop: true,
      align: 'center',
      startIndex,
      slidesToScroll: 1,
      dragThreshold: 8,
      duration: reducedMotion ? 0 : 32, // JL's snap-glide speed (Embla default 25)
      containScroll: false,
    });

    // Embla silently disables `loop` when the slides can't fill the viewport
    // (our 3 cards). Clone the set once so the loop engages and the arrows wrap
    // instead of clamping; with enough real cards this never runs. Skipped in
    // the theme editor so block selection maps 1:1 to rendered cards.
    const canLoop = this.embla.internalEngine?.().slideLooper?.canLoop() ?? true;
    if (!canLoop && this.cards.length > 1 && !window.Shopify?.designMode) {
      const container = this.querySelector('[data-lk-scroller]');
      this.cards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-lk-clone', '');
        clone.classList.remove('is-active');
        clone.removeAttribute('data-lk-motion');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        container.appendChild(clone);
      });
      this.cards = Array.from(this.querySelectorAll('[data-lk-card]'));
      this.embla.reInit();
    }

    this.handlePrev = () => this.embla.scrollPrev();
    this.handleNext = () => this.embla.scrollNext();
    this.prevBtn?.addEventListener('click', this.handlePrev);
    this.nextBtn?.addEventListener('click', this.handleNext);

    this.handleSelect = () => this.syncActive(true);
    this.embla.on('select', this.handleSelect);
    this.embla.on('reInit', () => this.syncActive(false));
    this.syncActive(false);
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.handlePrev);
    this.nextBtn?.removeEventListener('click', this.handleNext);
    this.embla?.destroy();
    this.embla = null;
  }

  syncActive(announce) {
    if (!this.embla) return;
    const index = this.embla.selectedScrollSnap();
    this.cards.forEach((card, i) => {
      card.classList.toggle('is-active', i === index);
    });
    // Loop keeps both directions available; Embla reports edges when it can't loop.
    if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', !this.embla.canScrollPrev());
    if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', !this.embla.canScrollNext());
    if (announce && this.statusEl) {
      const flavor = this.cards[index]?.dataset.flavor;
      if (flavor) this.statusEl.textContent = flavor;
    }
  }
}

if (!customElements.get('lk-flavor-carousel')) {
  customElements.define('lk-flavor-carousel', LkFlavorCarousel);
}
